/**
 * 🏠 Constantes para separação de famílias de produtos
 *
 * REGRA DE NEGÓCIO:
 * - Produtos FIXA BÁSICA tramitam no quadro TRAMITACAO_VIVO
 * - Produtos MÓVEL tramitam no quadro TRAMITACAO_PRIME
 * - Quando uma proposta tem ambas as famílias, cria 2 pedidos separados
 */

import type { TipoLinha, LinhaComposicao } from './planos-cnpj.js';

/**
 * Família do produto - determina o quadro de tramitação
 */
export type FamiliaTramitacao = 'FIXA_BASICA' | 'MOVEL';

/**
 * Tipos de linha que pertencem à família FIXA BÁSICA
 * Tramitam no quadro TRAMITACAO_VIVO
 */
export const TIPOS_FIXA_BASICA: TipoLinha[] = ['FIBRA', 'TV'];

/**
 * Tipos de linha que pertencem à família MÓVEL
 * Tramitam no quadro TRAMITACAO_PRIME
 */
export const TIPOS_MOVEL: TipoLinha[] = ['PORTABILIDADE', 'TT', 'TT_PRE', 'NOVA', 'LINHA_NOVA', 'RENOVACAO'];

/**
 * Determina a família de uma linha baseado no seu tipo
 *
 * Casos especiais:
 * - FIBRA com tipoFibra='TT' → TT FIXA → FIXA_BASICA
 */
export function determinarFamiliaLinha(linha: LinhaComposicao): FamiliaTramitacao {
  // FIBRA sempre é FIXA_BASICA (inclui TT FIXA que é tipoFibra='TT')
  if (linha.tipo === 'FIBRA') {
    return 'FIXA_BASICA';
  }

  // TV é FIXA_BASICA
  if (linha.tipo === 'TV') {
    return 'FIXA_BASICA';
  }

  // Todos os outros são MÓVEL
  return 'MOVEL';
}

/**
 * Interface para resultado da separação
 */
export interface LinhasSeparadas {
  fixaBasica: LinhaComposicao[];  // FIBRA, TV, TT FIXA
  movel: LinhaComposicao[];       // PN, TT, HA, MP, PASSAPORT
}

/**
 * Separa as linhas da proposta em duas famílias
 *
 * @param linhas - Array de linhas da composição
 * @returns Objeto com duas arrays: fixaBasica e movel
 */
export function separarLinhasPorFamilia(linhas: LinhaComposicao[]): LinhasSeparadas {
  const fixaBasica: LinhaComposicao[] = [];
  const movel: LinhaComposicao[] = [];

  for (const linha of linhas) {
    const familia = determinarFamiliaLinha(linha);

    if (familia === 'FIXA_BASICA') {
      fixaBasica.push(linha);
    } else {
      movel.push(linha);
    }
  }

  console.log('📊 [FAMILIAS] Separação de linhas:', {
    totalLinhas: linhas.length,
    fixaBasica: fixaBasica.length,
    movel: movel.length,
    tiposFixa: fixaBasica.map(l => l.tipo),
    tiposMovel: movel.map(l => l.tipo),
  });

  return { fixaBasica, movel };
}

/**
 * Verifica se a proposta precisa ser dividida em múltiplos pedidos
 */
export function propostaPrecisaDivisao(linhas: LinhaComposicao[]): boolean {
  const { fixaBasica, movel } = separarLinhasPorFamilia(linhas);
  return fixaBasica.length > 0 && movel.length > 0;
}
